import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import { Package, FolderOpen, ShoppingCart, ArrowRight, Warehouse, Users, Ticket, AlertTriangle, XCircle, Clock, Trash2, CheckCircle, Building2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [expiringBatches, setExpiringBatches] = useState([]);
  const [inventoryWarnings, setInventoryWarnings] = useState([]);
  const [loadingExpiring, setLoadingExpiring] = useState(true);

  useEffect(() => {
    fetchExpiringBatches();
    fetchInventoryWarnings();
  }, []);

  const fetchExpiringBatches = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/expiring-batches`, {
        params: { days: 12 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpiringBatches(response.data || []);
    } catch (error) {
      console.error("Errore caricamento scadenze:", error);
    } finally {
      setLoadingExpiring(false);
    }
  };

  const fetchInventoryWarnings = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/inventory-warnings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventoryWarnings(response.data || []);
    } catch (error) {
      console.error("Errore caricamento avvisi:", error);
    }
  };

  const handleDeleteBatch = async (productId, batchId) => {
    if (!window.confirm("Eliminare questo lotto?")) return;
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/products/${productId}/batches/${batchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Lotto eliminato");
      setExpiringBatches(prev => prev.filter(b => b.id !== batchId));
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const expired = expiringBatches.filter(b => b.is_expired);
  const expiringSoon = expiringBatches.filter(b => !b.is_expired && b.days_left <= 5);
  const expiringLater = expiringBatches.filter(b => !b.is_expired && b.days_left > 5);

  const cards = [
    { title: "Ordini", description: "Visualizza e gestisci gli ordini", icon: ShoppingCart, color: "red", path: "/admin/ordini" },
    { title: "Magazzino", description: "Gestisci quantità e lotti", icon: Warehouse, color: "blue", path: "/admin/magazzino" },
    { title: "Scadenze", description: "Controlla lotti in scadenza", icon: Clock, color: "yellow", path: "/admin/scadenze" },
    { title: "Prodotti", description: "Aggiungi e modifica i piatti", icon: Package, color: "green", path: "/admin/prodotti" },
    { title: "Categorie", description: "Gestisci le categorie", icon: FolderOpen, color: "green", path: "/admin/categorie" },
    { title: "Clienti", description: "Iscritti e punti fedeltà", icon: Users, color: "purple", path: "/admin/clienti" },
    { title: "Codici Sconto", description: "Coupon promozionali", icon: Ticket, color: "orange", path: "/admin/codici-sconto" },
    { title: "Spesa Partner", description: "Spesa mensile aziende", icon: Building2, color: "blue", path: "/admin/spesa-partner" },
    { title: "Ordine Rapido", description: "Ordina per conto di un cliente", icon: ShoppingCart, color: "orange", path: "/admin/ordine-rapido" },
  ];

  const colorClasses = {
    green: "bg-green-500",
    yellow: "bg-yellow-500",
    red: "bg-red-500",
    blue: "bg-blue-500",
    purple: "bg-purple-500",
    orange: "bg-orange-500",
  };

  return (
    <AdminLayout title="Dashboard" data-testid="admin-dashboard">
      {/* Alert Scadenze */}
      {!loadingExpiring && expiringBatches.length > 0 && (
        <div className="mb-6 space-y-3" data-testid="expiring-alert">
          {/* Header alert */}
          <div className={`rounded-lg p-4 border ${expired.length > 0 ? 'bg-red-500/15 border-red-500' : 'bg-orange-500/15 border-orange-500'}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                {expired.length > 0 ? (
                  <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                ) : (
                  <AlertTriangle className="w-6 h-6 text-orange-500 flex-shrink-0" />
                )}
                <div>
                  <h2 className={`font-bold text-lg ${expired.length > 0 ? 'text-red-400' : 'text-orange-400'}`}>
                    {expired.length > 0 
                      ? `${expired.length} lott${expired.length === 1 ? 'o' : 'i'} SCADUT${expired.length === 1 ? 'O' : 'I'}!` 
                      : `${expiringBatches.length} lott${expiringBatches.length === 1 ? 'o' : 'i'} in scadenza`}
                  </h2>
                  <p className="text-zinc-400 text-sm">
                    {expired.length > 0 && `${expired.length} scaduti`}
                    {expired.length > 0 && expiringSoon.length > 0 && " · "}
                    {expiringSoon.length > 0 && `${expiringSoon.length} entro 5 giorni`}
                    {(expired.length > 0 || expiringSoon.length > 0) && expiringLater.length > 0 && " · "}
                    {expiringLater.length > 0 && `${expiringLater.length} entro 12 giorni`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate("/admin/scadenze")}
                className="text-sm font-semibold text-white bg-zinc-700 hover:bg-zinc-600 px-3 py-1.5 rounded-lg flex items-center gap-1 flex-shrink-0"
              >
                Gestisci
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Lista compatta dei lotti */}
            <div className="space-y-1.5 mt-3 max-h-56 overflow-y-auto">
              {[...expired, ...expiringSoon, ...expiringLater].map((batch) => {
                const isExp = batch.is_expired;
                const isCrit = !isExp && batch.days_left <= 5;
                const rowBg = isExp ? "bg-red-500/10" : isCrit ? "bg-orange-500/10" : "bg-yellow-500/10";
                const textCol = isExp ? "text-red-400" : isCrit ? "text-orange-400" : "text-yellow-400";
                const daysText = isExp 
                  ? `Scaduto ${Math.abs(batch.days_left)}g fa` 
                  : batch.days_left === 0 ? "OGGI" 
                  : `${batch.days_left}g`;
                const scadDate = new Date(batch.scadenza).toLocaleDateString("it-IT", {day: "2-digit", month: "2-digit"});

                return (
                  <div key={batch.id} className={`flex items-center justify-between rounded-lg px-3 py-2 ${rowBg}`}>
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className={`font-bold text-xs px-1.5 py-0.5 rounded ${isExp ? 'bg-red-500 text-white' : isCrit ? 'bg-orange-500 text-black' : 'bg-yellow-500 text-black'}`}>
                        {daysText}
                      </span>
                      <span className="text-white font-medium truncate">{batch.product_name}</span>
                      <span className="text-zinc-500 text-sm hidden sm:inline">Lotto {batch.lotto}</span>
                      <span className="text-zinc-500 text-sm hidden sm:inline">Scad. {scadDate}</span>
                      <span className={`text-sm font-semibold ${textCol}`}>{batch.quantita_disponibile} pz</span>
                    </div>
                    <button
                      onClick={() => handleDeleteBatch(batch.product_id, batch.id)}
                      className="text-red-400 hover:text-red-300 p-1 ml-2 flex-shrink-0"
                      title="Elimina lotto"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Nessuna scadenza */}
      {!loadingExpiring && expiringBatches.length === 0 && (
        <div className="mb-6 bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center gap-3" data-testid="no-expiring">
          <CheckCircle className="w-6 h-6 text-green-500" />
          <p className="text-green-400 font-medium">Nessun prodotto in scadenza nei prossimi 12 giorni</p>
        </div>
      )}

      {/* Avvisi Inventario: prodotti senza lotto / scadenze anomale */}
      {inventoryWarnings.length > 0 && (
        <div className="mb-6" data-testid="inventory-warnings">
          <div className="bg-purple-500/15 border border-purple-500 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Package className="w-6 h-6 text-purple-400 flex-shrink-0" />
              <h2 className="text-purple-400 font-bold text-lg">
                {inventoryWarnings.length} avvis{inventoryWarnings.length === 1 ? 'o' : 'i'} inventario
              </h2>
            </div>
            <div className="space-y-1.5 mt-3 max-h-48 overflow-y-auto">
              {inventoryWarnings.map((w, idx) => (
                <div key={w.product_id || idx} className={`flex items-center justify-between rounded-lg px-3 py-2 ${
                  w.type === "no_batch" ? "bg-blue-500/10" : "bg-red-500/10"
                }`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span className={`font-bold text-xs px-1.5 py-0.5 rounded whitespace-nowrap ${
                      w.type === "no_batch" ? "bg-blue-500 text-white" : "bg-red-500 text-white"
                    }`}>
                      {w.type === "no_batch" ? "NO LOTTO" : "ANNO"}
                    </span>
                    <span className="text-white font-medium truncate">{w.product_name}</span>
                    {w.type === "no_batch" && (
                      <span className="text-blue-400 text-sm">{w.stock} pz senza tracciabilità</span>
                    )}
                    {w.type === "invalid_year" && (
                      <>
                        <span className="text-zinc-500 text-sm">Lotto {w.lotto}</span>
                        <span className="text-red-400 text-sm font-semibold">Scad. {w.scadenza}</span>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => navigate("/admin/magazzino")}
                    className="text-zinc-400 hover:text-white text-xs font-semibold ml-2 flex-shrink-0"
                  >
                    Correggi →
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.path}
              onClick={() => navigate(card.path)}
              className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 hover:border-zinc-700 transition-all cursor-pointer group"
              data-testid={`dashboard-card-${card.title.toLowerCase()}`}
            >
              <div className={`inline-flex p-4 rounded-lg ${colorClasses[card.color]} mb-4`}>
                <Icon className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-green-500 transition-colors">
                {card.title}
              </h3>
              <p className="text-zinc-400 mb-4">{card.description}</p>
              <div className="flex items-center gap-2 text-green-500 font-semibold">
                Gestisci
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          );
        })}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
